import { compareDates, localDate, type LocalDate } from '../dates';
import { tallyDay, type DayTally } from './daily';
import { endOfLocalDay } from './dayMath';
import { costOf } from './price';
import { limitOn, phaseOn, quitDateOf } from './taper';
import type { QuitPlan, SmokeLogLike } from './types';

export interface LimitProgress {
  /** Completed taper days at or under that day's limit (cumulative; a slip never takes days away). */
  daysUnder: number;
  daysCounted: number;
  bestRun: number;
  currentRun: number;
}

const completed = (date: LocalDate, from: LocalDate, today: LocalDate) =>
  compareDates(date, from) >= 0 && compareDates(date, today) < 0;

/** Over completed days (plan start up to yesterday) before the quit date. */
export function limitProgress(
  plan: Pick<QuitPlan, 'startDate' | 'taper'>,
  tallies: readonly DayTally[],
  today: LocalDate,
): LimitProgress {
  let daysUnder = 0;
  let daysCounted = 0;
  let run = 0;
  let bestRun = 0;
  const days = [...tallies].sort((a, b) => compareDates(a.date, b.date));
  for (const t of days) {
    if (!completed(t.date, plan.startDate, today) || phaseOn(plan.taper, t.date) !== 'taper')
      continue;
    const limit = limitOn(plan.taper, t.date);
    if (limit === null) continue;
    daysCounted += 1;
    if (t.count <= limit) {
      daysUnder += 1;
      run += 1;
      bestRun = Math.max(bestRun, run);
    } else {
      run = 0;
    }
  }
  return { daysUnder, daysCounted, bestRun, currentRun: run };
}

export interface SmokeFreeProgress {
  smokeFreeDays: number;
  daysSinceQuit: number;
}

/** Completed days from the quit date to yesterday: how many had no cigarettes. */
export function smokeFreeProgress(
  plan: Pick<QuitPlan, 'taper'>,
  tallies: readonly DayTally[],
  today: LocalDate,
): SmokeFreeProgress {
  const q = quitDateOf(plan.taper);
  if (q === null) return { smokeFreeDays: 0, daysSinceQuit: 0 };
  let smokeFreeDays = 0;
  let daysSinceQuit = 0;
  for (const t of tallies) {
    if (!completed(t.date, q, today)) continue;
    daysSinceQuit += 1;
    if (t.count === 0) smokeFreeDays += 1;
  }
  return { smokeFreeDays, daysSinceQuit };
}

/**
 * When the last counted cigarette was: the latest counted tap, or, for a day decided by a written
 * total, the time it was written (same day) or the end of that day (a backfill).
 */
export function lastSmokeAt(logs: readonly SmokeLogLike[], tz: string): string | null {
  const live = logs.filter((l) => l.deletedAt === null);
  const dates = [...new Set(live.map((l) => l.date))].sort().reverse();
  for (const d of dates) {
    const dayLogs = live.filter((l) => l.date === d);
    const t = tallyDay(dayLogs, d);
    if (t.count <= 0) continue;
    if (t.lastTapAt) return t.lastTapAt;
    const latest = dayLogs
      .filter((l) => l.kind === 'day_total')
      .sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0))
      .at(-1);
    if (!latest) continue;
    return localDate(latest.at, tz) === d ? latest.at : endOfLocalDay(d, tz);
  }
  return null;
}

export function durationParts(totalMinutes: number): {
  days: number;
  hours: number;
  minutes: number;
} {
  const m = Math.max(0, Math.floor(totalMinutes));
  return { days: Math.floor(m / 1440), hours: Math.floor((m % 1440) / 60), minutes: m % 60 };
}

export function spentMinor(tallies: readonly DayTally[]): number {
  return tallies.reduce((s, t) => s + t.costMinor, 0);
}

/**
 * Money saved against the baseline over completed days (plan start to yesterday). Both the baseline
 * and what was smoked are priced at the current price, so days logged before a price existed (or
 * at an older price) don't inflate it. Negative when more was smoked than the baseline.
 */
export function savedVsBaseline(
  plan: Pick<QuitPlan, 'baselinePerDay' | 'price' | 'startDate'>,
  tallies: readonly DayTally[],
  today: LocalDate,
): number {
  const baselineCost = costOf(plan.baselinePerDay, plan.price);
  if (baselineCost === 0) return 0;
  return tallies
    .filter((t) => completed(t.date, plan.startDate, today))
    .reduce((s, t) => s + baselineCost - costOf(t.count, plan.price), 0);
}
